// FLAGS: health-data, personal-data, identity, irreversible, silent-failure
// Every read and write of a client record staff can reach: identity, employer, check-in scores, dossier note, 2FA.
import { AxiosError } from "axios";
import {
  QueryObserverSuccessResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiPaths, RESPONSE_STATUS } from "@src/constants";
import { Clients } from "@src/api/v1/Clients";
import {
  GetClientsParams,
  ModelsQuestionsQuestionList,
  RequestClientCreateRequest,
  RequestClientUpdateRequest,
  ResponseHostClientWithRelationshipResponse,
} from "@src/api/v1/data-contracts";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";
import { useAuthentication } from "@src/components/wrappers/AuthenticationProvider";
import { fiveMinutes } from "./staletimes";
import { queryIdentifiers } from "../utils/constants";

export const useClientsQuery = (queryParams: GetClientsParams = {}) => {
  const { isLoggedIn } = useAuthentication();

  return useQuery({
    queryKey: [queryIdentifiers.clients, queryParams],
    queryFn: () => new Clients(OpenUpHttpClient).getClients(queryParams),
    enabled: isLoggedIn,
    staleTime: fiveMinutes,
  });
};
export const useMyClientsQuery = (
  queryParams: {
    page: number;
    search: string;
  } = { page: 0, search: "" },
) => {
  const { isLoggedIn } = useAuthentication();

  return useQuery({
    queryKey: [queryIdentifiers.myClients, queryParams],
    queryFn: () =>
      new Clients(OpenUpHttpClient).getPsychologistClients({
        page: queryParams.page,
        search: queryParams.search,
      }),
    enabled: isLoggedIn,
    staleTime: fiveMinutes,
  });
};

export const useClientQuery = (clientId: string | undefined) => {
  const { isLoggedIn } = useAuthentication();
  return useQuery({
    queryKey: [queryIdentifiers.clients, clientId],
    queryFn: () => new Clients(OpenUpHttpClient).getClientById(clientId!),
    staleTime: fiveMinutes,
    enabled: !!clientId && isLoggedIn,
    suspense: false,
  }) as QueryObserverSuccessResult<ResponseHostClientWithRelationshipResponse>;
};

export const useClientTagsQuery = (clientId: string) => {
  const { isLoggedIn } = useAuthentication();
  return useQuery({
    queryKey: [queryIdentifiers.clients, clientId, "tags"],
    queryFn: () => new Clients(OpenUpHttpClient).getClientTags(clientId),
    staleTime: fiveMinutes,
    enabled: isLoggedIn,
  });
};

export const useClientEmotionsOverviewQuery = (
  userId: string,
  questionListName: string,
) =>
  useQuery({
    queryKey: [
      queryIdentifiers.clients,
      userId,
      queryIdentifiers.questionListAnswers,
      questionListName,
    ],
    queryFn: () =>
      new Clients(OpenUpHttpClient).getUserQuestionListAnswers(
        userId,
        questionListName,
      ),
    staleTime: fiveMinutes,
    suspense: false,
  }) as QueryObserverSuccessResult<ModelsQuestionsQuestionList[]>;

export const useClientCheckinOverviewQuery = (userId: string) =>
  useQuery({
    queryKey: [queryIdentifiers.clients, userId, queryIdentifiers.themes],
    queryFn: () =>
      new Clients(OpenUpHttpClient).getClientVitalityCheckinResultsSummary(
        userId,
      ),
    staleTime: fiveMinutes,
  });

// FLAGS: health-data, silent-failure
// Writes `[]` into the cache from the render body on a 404, so "this client has no medical check-in" and "the read
// failed with a 404" are indistinguishable downstream; the write also happens during render, not in an effect.
export const useClientMedicalCheckinOverviewQuery = (userId: string) => {
  const queryClient = useQueryClient();
  const key = [apiPaths.clients, userId, "medical-themes"];
  const result = useQuery({
    queryKey: key,
    queryFn: () =>
      new Clients(OpenUpHttpClient).getUserMedicalCheckinResults(userId),
  });
  if (
    (result.error as AxiosError)?.response?.status === RESPONSE_STATUS.NOT_FOUND
  ) {
    queryClient.setQueryData(key, []);
  }
  return result;
};

export const useClientThemeAnswersQuery = (userId: string, formName: string) =>
  useQuery({
    queryKey: [
      queryIdentifiers.clients,
      userId,
      queryIdentifiers.themes,
      formName,
    ],
    queryFn: () =>
      new Clients(OpenUpHttpClient).getClientVitalityCheckinResultsForTheme(
        userId,
        formName,
      ),
    staleTime: fiveMinutes,
  });

export const useClientMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      user: { id: string } & (
        | RequestClientCreateRequest
        | RequestClientUpdateRequest
      ),
    ) =>
      user.id
        ? new Clients(OpenUpHttpClient).updateClient(user.id, user)
        : new Clients(OpenUpHttpClient).createClient(user),
    onSuccess: (data) => {
      queryClient.setQueryData([queryIdentifiers.clients, data.id], data);
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.clients],
      });
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.themes],
      });
    },
  });
};

// FLAGS: personal-data, irreversible
// Anonymises a client's account server-side. There is no undo and no confirmation in this hook — the only guard is
// whatever the calling screen puts in front of it.
export const useClientAnonymizeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) =>
      new Clients(OpenUpHttpClient).anonymizeClient(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.clients],
      });
    },
  });
};

export const useClientDossierNoteQuery = (clientId: string) =>
  useQuery({
    queryKey: [
      queryIdentifiers.clients,
      clientId,
      queryIdentifiers.dossierNote,
    ],
    queryFn: () => new Clients(OpenUpHttpClient).getClientDossierNote(clientId),
    staleTime: fiveMinutes,
  });

// FLAGS: health-data
// Overwrites the client's free-text clinical dossier note wholesale — the previous text is not merged or kept.
export const useClientDossierNoteMutation = (clientId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (value: string) =>
      new Clients(OpenUpHttpClient).updateClientDossierNote(clientId, {
        value,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [apiPaths.clients, clientId, queryIdentifiers.dossierNote],
      });
    },
  });
};

export const useClientUnlockMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => new Clients(OpenUpHttpClient).unlockUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.clients],
      });
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.psychologists],
      });
    },
  });
};

// FLAGS: identity
// Strips two-factor authentication from a client's login; who may do this is decided only by the API's policy.
export const useRemove2FAMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) =>
      new Clients(OpenUpHttpClient).removeTwoFactorFromUser(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.clients],
      });
    },
  });
};
